import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import BiodataPreview from "../components/BiodataPreview";
import PhotoUploader from "../components/PhotoUploader";
import TemplateThumbnails from "../components/TemplateThumbnails";
import { useAuth } from "../context/AuthContext";
import { api, downloadBinary, formatApiError } from "../lib/api";
import {
  EMPTY_BIODATA,
  PRIVACY_OPTIONS,
  WIZARD_STEPS,
  clearGuestBiodata,
  loadGuestBiodata,
  saveGuestBiodata,
} from "../lib/biodata";

/** Field metadata — drives the form auto-generation for each wizard step. */
const FIELDS = {
  personal: [
    { name: "full_name", label: "Full name", required: true },
    { name: "date_of_birth", label: "Date of birth", type: "date" },
    { name: "time_of_birth", label: "Time of birth", type: "time" },
    { name: "place_of_birth", label: "Place of birth" },
    { name: "gender", label: "Gender", type: "select", options: ["male", "female", "other"] },
    { name: "height", label: "Height (e.g. 5'8\")" },
    { name: "weight", label: "Weight (e.g. 68 kg)" },
    { name: "complexion", label: "Complexion", type: "select", options: ["Very Fair", "Fair", "Wheatish", "Wheatish Brown", "Dark"] },
    { name: "blood_group", label: "Blood group" },
    { name: "marital_status", label: "Marital status", type: "select", options: ["Never Married", "Divorced", "Widowed", "Awaiting Divorce"] },
    { name: "mother_tongue", label: "Mother tongue" },
    { name: "religion", label: "Religion" },
    { name: "caste", label: "Caste" },
    { name: "sub_caste", label: "Sub-caste" },
    { name: "gothra", label: "Gothra" },
    { name: "manglik", label: "Manglik", type: "select", options: ["No", "Yes", "Anshik", "Don't Know"] },
    { name: "rashi", label: "Rashi" },
    { name: "nakshatra", label: "Nakshatra" },
  ],
  family: [
    { name: "father_name", label: "Father's name" },
    { name: "father_occupation", label: "Father's occupation" },
    { name: "mother_name", label: "Mother's name" },
    { name: "mother_occupation", label: "Mother's occupation" },
    { name: "siblings", label: "Siblings (e.g. 1 brother, 1 sister)", type: "textarea" },
    { name: "family_type", label: "Family type", type: "select", options: ["Nuclear", "Joint"] },
    { name: "family_status", label: "Family status", type: "select", options: ["Middle Class", "Upper Middle Class", "Rich", "Affluent"] },
    { name: "family_values", label: "Family values", type: "select", options: ["Traditional", "Moderate", "Liberal"] },
    { name: "native_place", label: "Native place" },
  ],
  education_career: [
    { name: "highest_qualification", label: "Highest qualification" },
    { name: "field_of_study", label: "Field of study" },
    { name: "college", label: "College / University" },
    { name: "occupation", label: "Occupation" },
    { name: "designation", label: "Designation" },
    { name: "company", label: "Company" },
    { name: "annual_income", label: "Annual income" },
    { name: "work_location", label: "Work location" },
  ],
  lifestyle: [
    { name: "diet", label: "Diet", type: "select", options: ["Veg", "Non-Veg", "Eggetarian", "Vegan", "Jain"] },
    { name: "drinking", label: "Drinking", type: "select", options: ["No", "Occasional", "Yes"] },
    { name: "smoking", label: "Smoking", type: "select", options: ["No", "Occasional", "Yes"] },
    { name: "hobbies", label: "Hobbies" },
    { name: "languages_known", label: "Languages known" },
    { name: "about_me", label: "About me", type: "textarea" },
  ],
  partner_preferences: [
    { name: "age_range", label: "Age range" },
    { name: "height_range", label: "Height" },
    { name: "education", label: "Education" },
    { name: "occupation", label: "Occupation" },
    { name: "income", label: "Income" },
    { name: "religion", label: "Religion" },
    { name: "caste", label: "Caste" },
    { name: "location", label: "Location" },
    { name: "diet", label: "Diet" },
    { name: "marital_status", label: "Marital status" },
    { name: "other", label: "Other expectations", type: "textarea" },
  ],
  contact: [
    { name: "phone", label: "Phone" },
    { name: "email", label: "Email" },
    { name: "address", label: "Address", type: "textarea" },
    { name: "city", label: "City" },
    { name: "state", label: "State" },
    { name: "country", label: "Country" },
  ],
};

export default function BiodataBuilder() {
  const { id } = useParams(); // present when editing an existing biodata
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);
  const isAuthed = user && typeof user === "object";

  // Lazy initializer so the state is ALREADY the merged guest draft on the
  // very first render. This prevents the autosave effect (which runs on the
  // initial commit) from clobbering the localStorage draft with EMPTY_BIODATA
  // before the load effect has a chance to setBiodata(). Fixes the photo-drop
  // from hero being lost + preview flashing empty payloads.
  const [biodata, setBiodata] = useState(() => {
    if (isEditing) return EMPTY_BIODATA;
    const draft = loadGuestBiodata();
    const merged = draft ? { ...EMPTY_BIODATA, ...draft } : { ...EMPTY_BIODATA };
    const tplParam = searchParams.get("template");
    const religionParam = searchParams.get("religion");
    if (tplParam && ["classic", "modern", "royal"].includes(tplParam)) {
      merged.template = tplParam;
    }
    if (religionParam) {
      merged.personal = { ...merged.personal, religion: religionParam };
    }
    return merged;
  });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [savePrompt, setSavePrompt] = useState(false);
  const [loadingBiodata, setLoadingBiodata] = useState(isEditing);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  // Guard: only start autosaving to guest draft AFTER the async edit-mode
  // load has finished. Prevents clobbering when the user opens an existing
  // biodata for editing.
  const initializedRef = useRef(!isEditing);

  // Load existing biodata (edit mode only — guest is already handled by the
  // useState initializer above).
  useEffect(() => {
    if (!isEditing) return;
    (async () => {
      try {
        const { data } = await api.get(`/biodata/${id}`);
        setBiodata({ ...EMPTY_BIODATA, ...data });
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/dashboard");
      } finally {
        setLoadingBiodata(false);
        initializedRef.current = true;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Persist guest draft as user types. Only runs after init so the initial
  // empty state can't overwrite a freshly-populated draft.
  useEffect(() => {
    if (isEditing) return;
    if (!initializedRef.current) return;
    saveGuestBiodata(biodata);
  }, [biodata, isEditing]);

  // Mark initialized right after first commit for guest mode (already have
  // the merged state in `biodata`).
  useEffect(() => {
    if (!isEditing) initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = useCallback((section, name, value) => {
    setBiodata((prev) => ({
      ...prev,
      [section]: { ...prev[section], [name]: value },
    }));
  }, []);

  const updateTop = useCallback((name, value) => {
    setBiodata((prev) => ({ ...prev, [name]: value }));
  }, []);

  const currentStep = WIZARD_STEPS[step];
  const currentFields = FIELDS[currentStep.id] || [];

  const goNext = () => {
    if (step < WIZARD_STEPS.length - 1) setStep(step + 1);
    else setSavePrompt(true);
  };
  const goPrev = () => step > 0 && setStep(step - 1);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      await downloadBinary("/export/pdf", biodata, "biodata.pdf");
      toast.success("PDF downloaded");
      if (!isAuthed && !isEditing) setSavePrompt(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadDocx = async () => {
    setBusy(true);
    try {
      await downloadBinary("/export/docx", biodata, "biodata.docx");
      toast.success("DOCX downloaded");
      if (!isAuthed && !isEditing) setSavePrompt(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveToAccount = async () => {
    if (!isAuthed) {
      navigate("/register");
      return;
    }
    setBusy(true);
    try {
      if (isEditing) {
        await api.put(`/biodata/${id}`, biodata);
        toast.success("Saved");
      } else {
        const { data } = await api.post("/biodata", biodata);
        toast.success("Saved to your account");
        clearGuestBiodata();
        navigate(`/biodata/${data.id}/edit`);
      }
      setSavePrompt(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Calculate profile completion (client-side mirror of server heuristic).
  const completion = useMemo(() => {
    const checks = [
      biodata.personal.full_name,
      biodata.personal.date_of_birth,
      biodata.personal.gender,
      biodata.personal.religion,
      biodata.family.father_name,
      biodata.family.mother_name,
      biodata.education_career.highest_qualification,
      biodata.education_career.occupation,
      biodata.contact.phone || biodata.contact.email,
      biodata.photo_url,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [biodata]);

  if (loadingBiodata) {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card">Loading biodata…</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container-fluid px-3 px-lg-5 py-4" data-testid="biodata-builder">
        <div className="row g-4">
          {/* LEFT — form */}
          <div className="col-lg-6">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div className="sb-kicker mb-1">
                  {isEditing ? "Edit biodata" : "Guest builder"}
                </div>
                <h1 className="h3 mb-0" data-testid="builder-title">
                  {biodata.title || "My Biodata"}
                </h1>
              </div>
              <span className="sb-badge-soft" data-testid="builder-completion">
                {completion}% complete
              </span>
            </div>

            {/* Title + template + privacy row */}
            <div className="sb-card mt-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Biodata title</label>
                  <input
                    className="form-control"
                    value={biodata.title}
                    onChange={(e) => updateTop("title", e.target.value)}
                    data-testid="builder-title-input"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Photo</label>
                  <PhotoUploader
                    value={biodata.photo_url}
                    onChange={(v) => updateTop("photo_url", v)}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Template</label>
                  <TemplateThumbnails
                    value={biodata.template}
                    onChange={(v) => updateTop("template", v)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Privacy</label>
                  <select
                    className="form-select"
                    value={biodata.privacy}
                    onChange={(e) => updateTop("privacy", e.target.value)}
                    data-testid="builder-privacy"
                  >
                    {PRIVACY_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Steps */}
            <div className="sb-steps mt-4" data-testid="wizard-steps">
              {WIZARD_STEPS.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStep(i)}
                  className={
                    "sb-step" +
                    (i === step ? " active" : "") +
                    (i < step ? " done" : "")
                  }
                  data-testid={`step-${s.id}`}
                >
                  {i + 1}. {s.label}
                </button>
              ))}
            </div>

            <div className="sb-card">
              <h2 className="h5 mb-3">{currentStep.label}</h2>
              <div className="row g-3">
                {currentFields.map((f) => (
                  <FieldInput
                    key={f.name}
                    field={f}
                    value={biodata[currentStep.id][f.name] || ""}
                    onChange={(v) => updateField(currentStep.id, f.name, v)}
                  />
                ))}
              </div>

              <div className="d-flex justify-content-between mt-4 gap-2 flex-wrap">
                <button
                  className="btn btn-outline-primary"
                  onClick={goPrev}
                  disabled={step === 0}
                  data-testid="btn-prev"
                >
                  ← Previous
                </button>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-outline-primary"
                    onClick={downloadPdf}
                    disabled={busy}
                    data-testid="btn-download-pdf"
                  >
                    ↓ PDF
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={downloadDocx}
                    disabled={busy}
                    data-testid="btn-download-docx"
                  >
                    ↓ DOCX
                  </button>
                  {isAuthed && (
                    <button
                      className="btn btn-gold"
                      onClick={saveToAccount}
                      disabled={busy}
                      data-testid="btn-save"
                    >
                      {isEditing ? "Save changes" : "Save"}
                    </button>
                  )}
                  <button className="btn btn-primary" onClick={goNext} data-testid="btn-next">
                    {step === WIZARD_STEPS.length - 1 ? "Finish" : "Next →"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — sticky preview */}
          <div className="col-lg-6 d-none d-lg-block">
            <div style={{ position: "sticky", top: 90 }}>
              <BiodataPreview biodata={biodata} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile preview toggle */}
      <button
        type="button"
        className="btn btn-primary sb-mobile-preview-btn"
        onClick={() => setMobilePreviewOpen(true)}
        data-testid="mobile-preview-toggle"
      >
        Preview
      </button>

      {mobilePreviewOpen && (
        <div
          className="sb-preview-overlay"
          onClick={() => setMobilePreviewOpen(false)}
          data-testid="mobile-preview-overlay"
        >
          <div className="w-100 h-100" onClick={(e) => e.stopPropagation()}>
            <BiodataPreview biodata={biodata} />
            <button
              type="button"
              className="btn btn-outline-light position-absolute top-0 end-0 m-3"
              onClick={() => setMobilePreviewOpen(false)}
              style={{ zIndex: 60 }}
              data-testid="mobile-preview-close"
            >
              Close ×
            </button>
          </div>
        </div>
      )}

      {savePrompt && !isAuthed && (
        <SavePromptModal
          onClose={() => setSavePrompt(false)}
          onRegister={() => navigate("/register")}
        />
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const common = {
    className: "form-control",
    value,
    onChange: (e) => onChange(e.target.value),
    "data-testid": `field-${field.name}`,
  };
  const cols = field.type === "textarea" ? "col-12" : "col-md-6";
  return (
    <div className={cols}>
      <label className="form-label">
        {field.label}
        {field.required && <span className="text-danger"> *</span>}
      </label>
      {field.type === "select" ? (
        <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.name}`}>
          <option value="">— Select —</option>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : field.type === "textarea" ? (
        <textarea rows={3} {...common} />
      ) : (
        <input type={field.type || "text"} {...common} />
      )}
    </div>
  );
}

function SavePromptModal({ onClose, onRegister }) {
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ background: "rgba(26,22,20,0.55)", zIndex: 1050 }}
      data-testid="save-prompt-modal"
      onClick={onClose}
    >
      <div
        className="sb-card mx-3"
        style={{ maxWidth: 480 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sb-kicker mb-2">Almost done</div>
        <h2 className="h4 mb-2">Save this biodata to your account?</h2>
        <p className="text-muted">
          Create a free matrimonial profile to save, edit and share your biodata anytime.
        </p>
        <div className="d-flex gap-2 flex-wrap mt-3">
          <button className="btn btn-primary" onClick={onRegister} data-testid="save-prompt-register">
            Create free profile
          </button>
          <Link to="/login" className="btn btn-outline-primary" data-testid="save-prompt-login">
            I already have an account
          </Link>
          <button
            className="btn btn-link sb-link-quiet"
            onClick={onClose}
            data-testid="save-prompt-close"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
